import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, where, Timestamp, onSnapshot } from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import Button from '../../../components/ui/Button';
import Pagination from '../../../components/ui/Pagination';
import InputField from '../../../components/ui/InputField';
import { format, differenceInDays } from 'date-fns';
import CloseIcon from '../../../components/icons/CloseIcon';
import SearchIcon from '../../../components/icons/SearchIcon';
import TimerIcon from '../../../components/icons/TimerIcon';
import TrashIcon from '../../../components/icons/TrashIcon';
import RightArrowIcon from '../../../components/icons/RightArrowIcon';

interface Vote {
  id: string;
  title: string;
  description: string;
  options: string[];
  startDate: Date;
  endDate: Date;
  isActive: boolean;
  createdBy: string;
  schoolId: string;
  classId: string;
  voteType?: 'text' | 'date';
  multiSelect?: boolean;
  anonymous?: boolean;
  allowAddOption?: boolean;
  resultVisibility?: 'always' | 'afterVote' | 'afterClose' | 'private';
}

interface VoteResult {
  id: string;
  voteId: string;
  studentId: string;
  option: string;
  timestamp: Timestamp;
}

const VoteManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const [votes, setVotes] = useState<Vote[]>([]);
  const [voteResults, setVoteResults] = useState<VoteResult[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openResultsDialog, setOpenResultsDialog] = useState(false);
  const [selectedVote, setSelectedVote] = useState<Vote | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4; // 페이지당 4개 표시
  
  const [newVote, setNewVote] = useState<Partial<Vote>>({
    title: '',
    description: '',
    options: [''],
    startDate: new Date(),
    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    isActive: true,
    voteType: 'text',
    multiSelect: false,
    anonymous: false,
    allowAddOption: false,
    resultVisibility: 'always',
  });

  useEffect(() => {
    if (currentUser?.schoolId && currentUser?.classId) {
    fetchVotes();
    }
  }, [currentUser?.schoolId, currentUser?.classId]);

  useEffect(() => {
    if (votes.length > 0 && currentUser?.schoolId && currentUser?.classId) {
      fetchVoteResults();
    }
  }, [votes, currentUser?.schoolId, currentUser?.classId]);

  const fetchVotes = async () => {
    if (!currentUser?.schoolId || !currentUser?.classId) return;

    try {
      const votesRef = collection(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/votes`);
      const q = query(votesRef);
      const snapshot = await getDocs(q);
      const votesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        startDate: doc.data().startDate.toDate(),
        endDate: doc.data().endDate.toDate(),
      })) as Vote[];
      setVotes(votesData);
    } catch (error) {
      console.error('투표 목록 조회 실패:', error);
    }
  };

  const fetchVoteResults = async () => {
    if (!currentUser?.schoolId || !currentUser?.classId || votes.length === 0) return;

    try {
      const resultsRef = collection(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/voteResults`);
      const q = query(
        resultsRef,
        where('voteId', 'in', votes.map(vote => vote.id))
      );
      const snapshot = await getDocs(q);
      const resultsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as VoteResult[];
      setVoteResults(resultsData);
    } catch (error) {
      console.error('투표 결과 조회 실패:', error);
    }
  };

  // 실시간 업데이트를 위한 useEffect
  useEffect(() => {
    if (!currentUser?.schoolId || !currentUser?.classId || votes.length === 0) return;

    const resultsRef = collection(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/voteResults`);
    const q = query(
      resultsRef,
      where('voteId', 'in', votes.map(vote => vote.id))
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const resultsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as VoteResult[];
      setVoteResults(resultsData);
    });

    return () => unsubscribe();
  }, [votes, currentUser?.schoolId, currentUser?.classId]);

  // 필터링 및 검색된 투표 목록
  const filteredVotes = votes.filter(vote => {
    // 필터링
    if (filter === 'active' && !vote.isActive) return false;
    if (filter === 'completed' && vote.isActive) return false;
    
    // 검색
    if (searchQuery && !vote.title.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    return true;
  });

  // 페이지네이션
  const totalPages = Math.ceil(filteredVotes.length / itemsPerPage);
  const paginatedVotes = filteredVotes.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleAddOption = () => {
    setNewVote(prev => ({
      ...prev,
      options: [...(prev.options || []), ''],
    }));
  };

  const handleOptionChange = (index: number, value: string) => {
    setNewVote(prev => ({
      ...prev,
      options: prev.options?.map((option, i) => i === index ? value : option),
    }));
  };

  const handleRemoveOption = (index: number) => {
    setNewVote(prev => ({
      ...prev,
      options: prev.options?.filter((_, i) => i !== index),
    }));
  };

  const handleCreateVote = async () => {
    if (!currentUser?.schoolId || !currentUser?.classId) return;

    try {
      const voteData = {
        ...newVote,
        schoolId: currentUser.schoolId,
        classId: currentUser.classId,
        createdBy: currentUser.uid,
        startDate: Timestamp.fromDate(new Date(newVote.startDate!)),
        endDate: Timestamp.fromDate(new Date(newVote.endDate!)),
        voteType: newVote.voteType || 'text',
        multiSelect: !!newVote.multiSelect,
        anonymous: !!newVote.anonymous,
        allowAddOption: !!newVote.allowAddOption,
        resultVisibility: newVote.resultVisibility || 'always',
      };

      await addDoc(collection(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/votes`), voteData);
      setOpenDialog(false);
      setNewVote({
        title: '',
        description: '',
        options: [''],
        startDate: new Date(),
        endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        isActive: true,
        voteType: 'text',
        multiSelect: false,
        anonymous: false,
        allowAddOption: false,
        resultVisibility: 'always',
      });
      await fetchVotes();
    } catch (error) {
      console.error('투표 생성 실패:', error);
    }
  };

  const handleDeleteVote = async (voteId: string) => {
    if (!currentUser?.schoolId || !currentUser?.classId) return;
    try {
      await deleteDoc(doc(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/votes`, voteId));
      await fetchVotes();
    } catch (error) {
      console.error('투표 삭제 실패:', error);
    }
  };

  const handleToggleVoteStatus = async (voteId: string, currentStatus: boolean) => {
    if (!currentUser?.schoolId || !currentUser?.classId) return;
    try {
      await updateDoc(doc(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/votes`, voteId), {
        isActive: !currentStatus,
      });
      await fetchVotes();
    } catch (error) {
      console.error('투표 상태 변경 실패:', error);
    }
  };

  const handleViewResults = (vote: Vote) => {
    setSelectedVote(vote);
    setOpenResultsDialog(true);
  };

  const getVoteCount = (voteId: string, option: string) => {
    return voteResults.filter(result => result.voteId === voteId && result.option === option).length;
  };

  const getTotalVotes = (voteId: string) => {
    return voteResults.filter(result => result.voteId === voteId).length;
  };

  const canShowResults = (vote: Vote) => {
    if (!vote.resultVisibility || vote.resultVisibility === 'always') return true;
    if (vote.resultVisibility === 'afterVote') return true;
    if (vote.resultVisibility === 'afterClose') return vote.isActive === false;
    if (vote.resultVisibility === 'private') return false;
    return true;
  };

  const activeVotesCount = votes.filter(v => v.isActive).length;

  // 남은 일수 계산
  const getRemainingDays = (endDate: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);
    const diff = differenceInDays(end, today);
    return diff > 0 ? diff : 0;
  };

  // 참여자 수 계산 (중복 제거)
  const getParticipantCount = (voteId: string) => {
    const participants = new Set(voteResults.filter(result => result.voteId === voteId).map(result => result.studentId));
    return participants.size;
  };

  return (
    <div style={{
      width: '100%',
      height: '100%',
      position: 'relative',
      background: '#F4F6FA',
      display: 'flex',
      flexDirection: 'column',
      gap: '20px'
    }}>
        {/* 헤더 */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '32px'
        }}>
          <div style={{
            color: '#161616',
            fontSize: '32px',
            fontFamily: 'Pretendard',
            fontWeight: '600',
            lineHeight: '48px'
          }}>
            투표
          </div>
          
          {/* 통계 및 필터, 버튼 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              {/* 통계 및 필터 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span style={{
                    color: '#161616',
                    fontSize: '18px',
                    fontFamily: 'Pretendard',
                    fontWeight: '600',
                    lineHeight: '27px'
                  }}>총</span>
                  <span style={{
                    color: '#FF6F0F',
                    fontSize: '18px',
                    fontFamily: 'Pretendard',
                    fontWeight: '600',
                    lineHeight: '27px'
                  }}>{activeVotesCount}</span>
                  <span style={{
                    color: '#161616',
                    fontSize: '18px',
                    fontFamily: 'Pretendard',
                    fontWeight: '600',
                    lineHeight: '27px'
                  }}>개의 투표가 진행중입니다</span>
                </div>
                
                {/* 필터 버튼들 */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div
                    onClick={() => setFilter('all')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      background: filter === 'all' ? 'transparent' : 'transparent'
                    }}
                  >
                    <div style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      border: `2px solid ${filter === 'all' ? '#FF6F0F' : '#BABABA'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {filter === 'all' && (
                        <div style={{
                          width: '12.8px',
                          height: '12.8px',
                          borderRadius: '50%',
                          background: '#FF6F0F'
                        }} />
                      )}
                    </div>
                    <span style={{
                      color: filter === 'all' ? '#161616' : '#989898',
                      fontSize: '12px',
                      fontFamily: 'Pretendard',
                      fontWeight: '400',
                      lineHeight: '18px'
                    }}>전체</span>
                  </div>
                  
                  <div
                    onClick={() => setFilter('active')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: '4px'
                    }}
                  >
                    <div style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      border: `2px solid ${filter === 'active' ? '#FF6F0F' : '#BABABA'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {filter === 'active' && (
                        <div style={{
                          width: '12.8px',
                          height: '12.8px',
                          borderRadius: '50%',
                          background: '#FF6F0F'
                        }} />
                      )}
                    </div>
                    <span style={{
                      color: filter === 'active' ? '#161616' : '#989898',
                      fontSize: '12px',
                      fontFamily: 'Pretendard',
                      fontWeight: '400',
                      lineHeight: '18px'
                    }}>진행 중</span>
                  </div>
                  
                  <div
                    onClick={() => setFilter('completed')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: '4px'
                    }}
                  >
                    <div style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      border: `2px solid ${filter === 'completed' ? '#FF6F0F' : '#BABABA'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {filter === 'completed' && (
                        <div style={{
                          width: '12.8px',
                          height: '12.8px',
                          borderRadius: '50%',
                          background: '#FF6F0F'
                        }} />
                      )}
                    </div>
                    <span style={{
                      color: filter === 'completed' ? '#161616' : '#989898',
                      fontSize: '12px',
                      fontFamily: 'Pretendard',
                      fontWeight: '400',
                      lineHeight: '18px'
                    }}>완료</span>
                  </div>
                </div>
              </div>
              
              {/* 투표 만들기 버튼 */}
        <Button
                variant="submit"
          onClick={() => setOpenDialog(true)}
                width="118px"
                height="40px"
        >
                투표 만들기
        </Button>
            </div>
            
            {/* 검색 바 */}
            <div style={{
              height: '40px',
              position: 'relative',
              borderRadius: '999px',
              background: 'white',
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              border: '1px solid #E9E9E9'
            }}>
              <SearchIcon width={20} height={20} color="#222222" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="투표명 검색"
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  fontSize: '13px',
                  fontFamily: 'Pretendard',
                  fontWeight: '400',
                  lineHeight: '19.5px',
                  color: '#989898',
                  background: 'transparent'
                }}
              />
            </div>
          </div>
        </div>
        
        {/* 투표 목록 */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          {paginatedVotes.length === 0 ? (
            <div style={{
              width: '100%',
              height: '535px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.05)',
              borderRadius: '12px',
              border: '1px solid #E9E9E9'
            }}>
              <div style={{
                textAlign: 'center',
                color: '#161616',
                fontSize: '18px',
                fontFamily: 'Pretendard',
                fontWeight: '500',
                lineHeight: '27px'
              }}>
                진행 중인 투표가 없습니다😥<br/>투표를 생성해 주세요.
              </div>
            </div>
          ) : (
            <div style={{
              width: '100%',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              {paginatedVotes.map((vote) => {
                const participantCount = getParticipantCount(vote.id);
                const totalVotes = getTotalVotes(vote.id);
                const remainingDays = vote.isActive ? getRemainingDays(vote.endDate) : 0;
                
                return (
                  <div
                    key={vote.id}
                    style={{
                      background: 'white',
                      borderRadius: '16px',
                      padding: '20px',
                      boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05), 0px 0px 5px 0px rgba(0, 0, 0, 0.05)',
                      width: '308px',
                      height: '535px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '52px'
                    }}
                  >
                    {/* 헤더 섹션 */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '20px'
                    }}>
                      {/* 상태 태그 및 날짜 */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div style={{
                          padding: '2px 12px',
                          borderRadius: '8px',
                          background: vote.isActive ? '#FFD2B5' : 'white',
                          border: vote.isActive ? 'none' : '1px solid #F0F0F0'
                        }}>
                          <span style={{
                            color: vote.isActive ? '#FF6F0F' : '#161616',
                            fontSize: '12px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            lineHeight: '18px'
                          }}>
                            {vote.isActive ? '진행중' : '완료'}
                          </span>
                        </div>
                        <div style={{
                          color: '#989898',
                          fontSize: '12px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '18px'
                        }}>
                          {format(vote.startDate, 'yyyy.MM.dd')}~{format(vote.endDate, 'yyyy.MM.dd')}
                        </div>
                      </div>
                      
                      {/* 제목 및 설명 */}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px'
                      }}>
                        <div style={{
                          color: '#161616',
                          fontSize: '18px',
                          fontFamily: 'Pretendard',
                          fontWeight: '600',
                          lineHeight: '27px'
                        }}>
                          {vote.title}
                        </div>
                        {vote.description && (
                          <div style={{
                            color: '#4C4C4C',
                            fontSize: '13px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            lineHeight: '19.5px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}>
                            {vote.description}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* 결과 섹션 */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                      flex: 1
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end'
                      }}>
                        <div style={{
                          color: '#4C4C4C',
                          fontSize: '14px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '21px'
                        }}>
                          결과
                        </div>
                        <div
                          onClick={() => handleViewResults(vote)}
                          style={{
                            color: '#989898',
                            fontSize: '12px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            lineHeight: '18px',
                            cursor: 'pointer'
                          }}
                        >
                          자세히 보기
                        </div>
                      </div>
                      
                      <div style={{
                        border: '1px solid #F0F0F0',
                        borderRadius: '8px',
                        padding: '16px',
                        height: '244px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px'
                      }}>
                        {/* 참여자 수 */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span style={{
                            color: '#FF6F0F',
                            fontSize: '12px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            lineHeight: '18px'
                          }}>
                            {participantCount}명
                          </span>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '2px'
                          }}>
                            <span style={{
                              color: '#989898',
                              fontSize: '12px',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              lineHeight: '18px'
                            }}>
                              참여중
                            </span>
                            <RightArrowIcon width={16} height={16} color="#989898" />
                          </div>
                        </div>
                        
                        {/* 투표 항목들 */}
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '28px',
                          flex: 1
                        }}>
                          {vote.options.slice(0, 3).map((option, index) => {
                            const count = getVoteCount(vote.id, option);
                            const percentage = totalVotes > 0 ? Math.round(count / totalVotes * 100) : 0;
                            
                            return (
                              <div key={index} style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px'
                              }}>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}>
                                  <span style={{
                                    color: '#4C4C4C',
                                    fontSize: '13px',
                                    fontFamily: 'Pretendard',
                                    fontWeight: '500',
                                    lineHeight: '19.5px'
                                  }}>
                                    항목 {String(index + 1).padStart(2, '0')}
                                  </span>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '5px'
                                  }}>
                                    <span style={{
                                      color: '#161616',
                                      fontSize: '13px',
                                      fontFamily: 'Pretendard',
                                      fontWeight: '500',
                                      lineHeight: '19.5px'
                                    }}>
                                      {count}표
                                    </span>
                                    <span style={{
                                      color: '#989898',
                                      fontSize: '13px',
                                      fontFamily: 'Pretendard',
                                      fontWeight: '500',
                                      lineHeight: '19.5px'
                                    }}>
                                      ({percentage}%)
                                    </span>
                                  </div>
                                </div>
                                {/* 진행 바 */}
                                <div style={{
                                  position: 'relative',
                                  width: '100%',
                                  height: '8px',
                                  background: '#E9E9E9',
                                  borderRadius: '999px',
                                  overflow: 'hidden'
                                }}>
                                  <div style={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    height: '100%',
                                    width: `${percentage}%`,
                                    background: '#293044',
                                    borderRadius: '999px'
                                  }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* 남은 일수 */}
                        {vote.isActive && remainingDays > 0 && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            justifyContent: 'flex-end'
                          }}>
                            <TimerIcon width={16} height={16} color="#FF6F0F" />
                            <span style={{
                              color: '#FF6F0F',
                              fontSize: '12px',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              lineHeight: '18px'
                            }}>
                              {remainingDays}일
                            </span>
                            <span style={{
                              color: '#989898',
                              fontSize: '12px',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              lineHeight: '18px'
                            }}>
                              후 종료
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* 하단 액션 버튼 */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div
                        onClick={() => handleDeleteVote(vote.id)}
                        style={{
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer'
                        }}
                      >
                        <TrashIcon width={20} height={20} color="#161616" />
                      </div>
                      <Button
                        variant={vote.isActive ? "submit" : "cancel"}
                        size="small"
                        onClick={() => handleToggleVoteStatus(vote.id, vote.isActive)}
                        width="auto"
                        height="32px"
                        style={{
                          padding: '0 12px',
                          background: vote.isActive ? '#293044' : '#E9E9E9',
                          color: vote.isActive ? 'white' : '#BABABA'
                        }}
                      >
                        종료
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* 페이지네이션 */}
          {paginatedVotes.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onChange={setCurrentPage}
              style={{ padding: '20px 0' }}
            />
          )}
        </div>

      {/* 새 투표 만들기 다이얼로그 */}
      {openDialog && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setOpenDialog(false);
            }
          }}
            >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px 28px',
              width: '688px',
              maxWidth: 'calc(100vw - 40px)',
              maxHeight: 'calc(100vh - 40px)',
              overflow: 'auto',
              boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '24px'
            }}>
              <div style={{
                fontSize: '20px',
                fontFamily: 'Pretendard',
                fontWeight: '600',
                color: '#161616'
              }}>
                새 투표 만들기
              </div>
              <div
                onClick={() => setOpenDialog(false)}
                style={{
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <CloseIcon width={24} height={24} />
              </div>
            </div>
            
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              <InputField
                value={newVote.title || ''}
                onChange={(v) => setNewVote(prev => ({ ...prev, title: v }))}
                placeholder="투표 제목"
                width="100%"
                height="48px"
              />
              
              <textarea
                value={newVote.description || ''}
                onChange={(e) => setNewVote(prev => ({ ...prev, description: e.target.value }))}
                placeholder="투표 설명"
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '12px',
                  border: '1px solid #F0F0F0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Pretendard',
                  resize: 'vertical'
                }}
              />
              
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontFamily: 'Pretendard',
                  fontWeight: '600',
                  color: '#161616'
                }}>
                  투표 옵션
                </label>
            {newVote.options?.map((option, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '8px',
                    alignItems: 'center'
                  }}>
                    <InputField
                    value={option}
                      onChange={(v) => handleOptionChange(index, v)}
                  placeholder={`옵션 ${index + 1}`}
                      width="100%"
                      height="48px"
                />
                {index > 0 && (
                      <Button
                        variant="line"
                        width="48px"
                        height="48px"
                        padding="0"
                        onClick={() => handleRemoveOption(index)}
                      >
                        삭제
                      </Button>
                )}
                  </div>
            ))}
            <Button
                  variant="outline"
              onClick={handleAddOption}
                  width="100px"
                  height="40px"
            >
              옵션 추가
            </Button>
              </div>
              
              <div style={{
                display: 'flex',
                gap: '12px'
              }}>
                <InputField
              type="datetime-local"
                                  value={newVote.startDate ? new Date(newVote.startDate).toISOString().slice(0, 16) : ''}
                onChange={(v) => setNewVote(prev => ({ ...prev, startDate: v ? new Date(v) : new Date() }))}
                  placeholder="시작일"
                  width="50%"
                  height="48px"
            />
                <InputField
              type="datetime-local"
                                  value={newVote.endDate ? new Date(newVote.endDate).toISOString().slice(0, 16) : ''}
                onChange={(v) => setNewVote(prev => ({ ...prev, endDate: v ? new Date(v) : new Date() }))}
                  placeholder="종료일"
                  width="50%"
                  height="48px"
            />
              </div>
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '24px'
            }}>
              <Button
                variant="cancel"
                onClick={() => setOpenDialog(false)}
                width="80px"
                height="40px"
              >
                취소
              </Button>
              <Button
                variant="submit"
                onClick={handleCreateVote}
                width="80px"
                height="40px"
              >
                생성
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 투표 결과 다이얼로그 */}
      {openResultsDialog && selectedVote && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setOpenResultsDialog(false);
            }
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px 28px',
              width: '688px',
              maxWidth: 'calc(100vw - 40px)',
              maxHeight: 'calc(100vh - 40px)',
              overflow: 'auto',
              boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)'
            }}
            onClick={(e) => e.stopPropagation()}
      >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '24px'
            }}>
              <div style={{
                fontSize: '20px',
                fontFamily: 'Pretendard',
                fontWeight: '600',
                color: '#161616'
              }}>
                {selectedVote.title} - 투표 결과
              </div>
              <div
                onClick={() => setOpenResultsDialog(false)}
                style={{
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <CloseIcon width={24} height={24} />
              </div>
            </div>
            
          {selectedVote && canShowResults(selectedVote) ? (
              <div>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse'
                }}>
                  <thead>
                    <tr style={{
                      borderBottom: '1px solid #E9E9E9'
                    }}>
                      <th style={{
                        textAlign: 'left',
                        padding: '12px',
                        fontSize: '14px',
                        fontFamily: 'Pretendard',
                        fontWeight: '600',
                        color: '#161616'
                      }}>옵션</th>
                      <th style={{
                        textAlign: 'right',
                        padding: '12px',
                        fontSize: '14px',
                        fontFamily: 'Pretendard',
                        fontWeight: '600',
                        color: '#161616'
                      }}>투표 수</th>
                      <th style={{
                        textAlign: 'right',
                        padding: '12px',
                        fontSize: '14px',
                        fontFamily: 'Pretendard',
                        fontWeight: '600',
                        color: '#161616'
                      }}>비율</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedVote && selectedVote.options.map((option) => {
                  const count = getVoteCount(selectedVote.id, option);
                  const total = getTotalVotes(selectedVote.id);
                  const percentage = total > 0 ? Math.round(count / total * 100) : 0;
                  
                  return (
                        <tr key={option} style={{
                          borderBottom: '1px solid #E9E9E9'
                        }}>
                          <td style={{
                            padding: '12px',
                            fontSize: '14px',
                            fontFamily: 'Pretendard',
                            color: '#161616'
                          }}>{option}</td>
                          <td style={{
                            textAlign: 'right',
                            padding: '12px',
                            fontSize: '14px',
                            fontFamily: 'Pretendard',
                            color: '#161616'
                          }}>{count}표</td>
                          <td style={{
                            textAlign: 'right',
                            padding: '12px',
                            fontSize: '14px',
                            fontFamily: 'Pretendard',
                            color: '#161616'
                          }}>{percentage}%</td>
                        </tr>
                  );
                })}
                    <tr style={{
                      fontWeight: '600',
                      background: '#F5F5F5'
                    }}>
                      <td style={{
                        padding: '12px',
                        fontSize: '14px',
                        fontFamily: 'Pretendard',
                        color: '#161616'
                      }}>총계</td>
                      <td style={{
                        textAlign: 'right',
                        padding: '12px',
                        fontSize: '14px',
                        fontFamily: 'Pretendard',
                        color: '#161616'
                      }}>{selectedVote ? getTotalVotes(selectedVote.id) : 0}표</td>
                      <td style={{
                        textAlign: 'right',
                        padding: '12px',
                        fontSize: '14px',
                        fontFamily: 'Pretendard',
                        color: '#161616'
                      }}>100%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
          ) : (
              <div style={{
                padding: '24px',
                textAlign: 'center',
                color: '#989898',
                fontSize: '14px',
                fontFamily: 'Pretendard'
              }}>
              결과가 공개되지 않은 투표입니다.
              </div>
          )}
            
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '24px'
            }}>
              <Button
                variant="submit"
                onClick={() => setOpenResultsDialog(false)}
                width="80px"
                height="40px"
              >
                닫기
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default VoteManagement; 